/**
 * BrandingContext - Resolves white label branding.
 * 1. On load: checks hostname for custom domain branding (pre-auth)
 * 2. After login: fetches org branding for logged-in user (post-auth)
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api/axios';

const BrandingContext = createContext(null);

const DEFAULT_BRANDING = {
    whitelabel: false,
    companyName: 'Email Lion',
    logoUrl: '',
    faviconUrl: '',
    primaryColor: '#1565C0',
    secondaryColor: '#42A5F5',
    loginBgColor: '',
    loginTitle: '',
    loginSubtitle: '',
    supportEmail: '',
    supportUrl: '',
    customCss: '',
    customDomain: '',
    orgId: null,
    planName: 'free',
    showPoweredBy: false,
};

const applyBrandingToDOM = (data) => {
    if (data.faviconUrl) {
        const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
        link.rel = 'icon';
        link.href = data.faviconUrl;
        document.head.appendChild(link);
    }
    if (data.companyName && data.companyName !== 'Email Lion') {
        document.title = data.companyName;
    }
    // Remove old custom CSS
    const oldStyle = document.getElementById('whitelabel-css');
    if (oldStyle) oldStyle.remove();
    if (data.customCss) {
        const style = document.createElement('style');
        style.id = 'whitelabel-css';
        style.textContent = data.customCss;
        document.head.appendChild(style);
    }
    // Prevent search engines from indexing white label domains
    if (data.whitelabel) {
        let robotsMeta = document.querySelector('meta[name="robots"]');
        if (!robotsMeta) {
            robotsMeta = document.createElement('meta');
            robotsMeta.name = 'robots';
            document.head.appendChild(robotsMeta);
        }
        robotsMeta.content = 'noindex, nofollow';
        // Also block googlebot and bingbot specifically
        ['googlebot', 'bingbot'].forEach(bot => {
            let m = document.querySelector(`meta[name="${bot}"]`);
            if (!m) { m = document.createElement('meta'); m.name = bot; document.head.appendChild(m); }
            m.content = 'noindex, nofollow';
        });
        // Remove canonical link (should not point to main domain)
        const canonical = document.querySelector('link[rel="canonical"]');
        if (canonical) canonical.remove();
    }
};

export const BrandingProvider = ({ children }) => {
    const [branding, setBranding] = useState(() => {
        try {
            const cached = sessionStorage.getItem('emailLion_branding');
            if (cached) return JSON.parse(cached);
        } catch (e) { /* ignore */ }
        return DEFAULT_BRANDING;
    });
    // If we have cached branding, don't block rendering — but still refresh in background
    const hasCached = (() => { try { return !!sessionStorage.getItem('emailLion_branding'); } catch { return false; } })();
    const [loading, setLoading] = useState(!hasCached);

    // Resolve by hostname on initial load (for custom domain / login page)
    useEffect(() => {
        const resolve = async () => {
            try {
                const hostname = window.location.hostname;
                if (hostname === 'localhost' || hostname === '127.0.0.1') {
                    setLoading(false);
                    return;
                }
                const baseUrl = window.location.origin;
                const res = await fetch(`${baseUrl}/api/branding/resolve?hostname=${encodeURIComponent(hostname)}`);
                if (res.ok) {
                    const data = await res.json();
                    if (data.whitelabel) {
                        setBranding(data);
                        sessionStorage.setItem('emailLion_branding', JSON.stringify(data));
                        applyBrandingToDOM(data);
                    }
                }
            } catch (e) {
                console.error('[Branding] Resolve error:', e);
            } finally {
                setLoading(false);
            }
        };
        resolve();
    }, []);

    // Load branding for the logged-in user's org (called after auth)
    const loadUserBranding = useCallback(async () => {
        try {
            const res = await api.get('/branding/my-org');
            if (res.data && res.data.whitelabel) {
                setBranding(res.data);
                sessionStorage.setItem('emailLion_branding', JSON.stringify(res.data));
                applyBrandingToDOM(res.data);
            }
        } catch (e) {
            // Not critical - branding is optional
        }
    }, []);

    // Reset branding to defaults (called on logout)
    const resetBranding = useCallback(() => {
        setBranding(DEFAULT_BRANDING);
        sessionStorage.removeItem('emailLion_branding');
        const oldStyle = document.getElementById('whitelabel-css');
        if (oldStyle) oldStyle.remove();
        document.title = 'Email Lion';
        // Restore default favicon
        const link = document.querySelector("link[rel~='icon']");
        if (link) link.href = '/favicon.png?v=5';
        // Restore robots meta to allow indexing (only relevant for main domain)
        const robotsMeta = document.querySelector('meta[name="robots"]');
        if (robotsMeta) robotsMeta.content = 'index, follow';
    }, []);

    return (
        <BrandingContext.Provider value={{ branding, loading, setBranding, loadUserBranding, resetBranding }}>
            {children}
        </BrandingContext.Provider>
    );
};

export const useBranding = () => useContext(BrandingContext) || { branding: DEFAULT_BRANDING, loading: false, loadUserBranding: () => {}, resetBranding: () => {} };
export default BrandingContext;
